import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { buildHandoverText, waLinkWithText } from '../utils/parkHandoverText';
import { useScrollRestore } from '../hooks/useScrollRestore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, Plus, Users, Phone, Calendar, MessageSquare, Search, RefreshCw,
  Trash2, AlertTriangle, ArrowUpDown, AlertCircle, CheckCircle2, Clock,
  MessageCircle, Zap, ChevronRight, CheckSquare, X,
} from 'lucide-react';
import { setBadge, updateTabTitle, refreshBadge } from '../services/badgeService';
import { useToast } from '../hooks/use-toast';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

// ── Priority chip config (source of truth for colours + icons) ──────────────
const PRIORITY_CONFIG = {
  overdue: {
    label: 'Overdue',
    className: 'bg-red-100 text-red-700 border border-red-200',
    Icon: AlertCircle,
  },
  ready_to_convert: {
    label: 'Ready to Convert',
    className: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
    Icon: CheckCircle2,
  },
  today: {
    label: 'Follow up today',
    className: 'bg-amber-100 text-amber-800 border border-amber-200',
    Icon: Clock,
  },
  later: {
    label: 'Later',
    className: 'bg-slate-100 text-slate-600 border border-slate-200',
    Icon: Calendar,
  },
};

const STATUS_COLORS = {
  open:   'bg-emerald-50 text-emerald-700',
  won:    'bg-green-600 text-white',
  lost:   'bg-red-100 text-red-700',
  closed: 'bg-gray-100 text-gray-600',
};

// Currency formatter (INR, compact for readability)
const formatValue = (n) => {
  if (!n) return null;
  const v = Number(n);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000)   return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000)     return `₹${(v / 1000).toFixed(0)}K`;
  return `₹${v.toFixed(0)}`;
};

// Initials for avatar
const initialsOf = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

// Compact WhatsApp-style timestamp
const formatWhen = (iso) => {
  if (!iso) return '';
  const dt = new Date(iso + (iso.endsWith('Z') ? '' : 'Z'));
  const now = new Date();
  const sameDay = dt.toDateString() === now.toDateString();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  const isYest = dt.toDateString() === yest.toDateString();
  if (sameDay) return dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  if (isYest)  return 'Yesterday';
  const diff = (now - dt) / 86400000;
  if (diff < 7) return dt.toLocaleDateString('en-IN', { weekday: 'short' });
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

// wa.me deep-link building is centralised in ../utils/parkHandoverText

const LEAD_SOURCES = ['Instagram', 'Call', 'J Hills'];

const LeadSessions = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [sessions, setSessions] = useState([]);
  const [totalMatching, setTotalMatching] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bgRefreshing, setBgRefreshing] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [salesmen, setSalesmen] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [salesmanFilter, setSalesmanFilter] = useState(searchParams.get('salesman') || '');
  const [quickFilter, setQuickFilter] = useState(searchParams.get('quick') || '');
  const [parkActionFilter, setParkActionFilter] = useState(searchParams.get('park') || '');
  const [parkActions, setParkActions] = useState([]);
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'priority');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sort_order') || 'desc');
  const [form, setForm] = useState({ assigned_salesman_id: '', lead_source: '', notes: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  // ── Bulk selection (Admin only) ──
  const isAdmin = currentUser?.role === 'admin';
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const listRef = useScrollRestore('sessions-list');
  const isMounted = useRef(false);

  // ── In-memory cache with TTL ──
  const CACHE_TTL = 60_000;
  const cache = useRef({ data: [], total: 0, truncated: false, timestamp: 0, filters: { status: '', sortBy: '', sortOrder: '' } });
  const invalidateCache = () => { cache.current.timestamp = 0; };

  // ── Swipe (mobile action-reveal): left-swipe reveals Call / WhatsApp ──
  // deltaX < 0 while swiping. Threshold to lock open = -140px, snap-back otherwise.
  const [swipe, setSwipe] = useState({ id: null, dx: 0 });
  const [openSwipeId, setOpenSwipeId] = useState(null); // row locked open showing actions
  const touchStart = useRef({ x: 0, y: 0 });
  const swipeActive = useRef(false);
  const swipeDxRef = useRef(0);

  const closeSwipe = useCallback(() => {
    setOpenSwipeId(null);
    setSwipe({ id: null, dx: 0 });
  }, []);

  const handleTouchStart = useCallback((e, id) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    swipeActive.current = false;
    swipeDxRef.current = 0;
    setSwipe({ id, dx: openSwipeId === id ? -140 : 0 });
  }, [openSwipeId]);

  const handleTouchMove = useCallback((e, id, hasUnread) => {
    const dx = e.touches[0].clientX - touchStart.current.x;
    const dy = e.touches[0].clientY - touchStart.current.y;
    if (!swipeActive.current && Math.abs(dy) > Math.abs(dx) * 0.7) {
      // vertical scroll intent — cancel swipe
      setSwipe({ id: null, dx: 0 });
      swipeDxRef.current = 0;
      return;
    }
    let effectiveDx = dx;
    if (openSwipeId === id) effectiveDx = dx - 140; // continue from open state
    if (Math.abs(effectiveDx) > 12) swipeActive.current = true;

    // Left-swipe: reveal Call/WhatsApp (up to -180px)
    // Right-swipe: reveal "Mark as read" ONLY for unread sessions (up to +160px)
    let clamped;
    if (effectiveDx < 0) {
      clamped = Math.max(effectiveDx, -180);
    } else if (hasUnread) {
      clamped = Math.min(effectiveDx, 160);
    } else {
      // No unread → don't allow right-swipe drag
      clamped = 0;
    }
    swipeDxRef.current = clamped;
    setSwipe({ id, dx: clamped });
  }, [openSwipeId]);

  const markRead = useCallback(async (sessionId) => {
    setSessions(prev => prev.map(s => s._id === sessionId ? { ...s, has_unread: false } : s));
    try {
      await api.post(`/crm/sessions/${sessionId}/mark-read`);
      refreshBadge();
    } catch (e) { /* non-critical */ }
  }, []);

  const handleTouchEnd = useCallback((id, hasUnread) => {
    const dx = swipeDxRef.current;
    if (dx < -80) {
      // Lock open showing left-side actions (Call/WhatsApp)
      setOpenSwipeId(id);
      setSwipe({ id, dx: -140 });
    } else if (dx > 60 && hasUnread) {
      // Right-swipe past threshold → mark as read, snap back
      markRead(id);
      closeSwipe();
    } else {
      closeSwipe();
    }
    swipeDxRef.current = 0;
  }, [closeSwipe, markRead]);

  const fetchSessions = async (force = false) => {
    const now = Date.now();
    const age = now - cache.current.timestamp;
    const hasCached = cache.current.data.length > 0;
    const isFresh = age < CACHE_TTL;
    const filtersMatch = cache.current.filters.status === statusFilter &&
                        cache.current.filters.sortBy === sortBy &&
                        cache.current.filters.sortOrder === sortOrder;

    if (!force && hasCached && isFresh && filtersMatch) {
      setSessions(cache.current.data);
      setTotalMatching(cache.current.total);
      setTruncated(cache.current.truncated);
      setLoading(false);
      return;
    }
    if (!hasCached || !filtersMatch) setLoading(true); else setBgRefreshing(true);

    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      // Backend sort — 'priority' is a UI-side sort, translate to last_activity server-side
      params.set('sort_by', sortBy === 'priority' ? 'last_activity' : sortBy);
      params.set('sort_order', sortOrder);
      const res = await api.get(`/crm/sessions?${params.toString()}`);
      // Backend now returns { sessions, total, returned, truncated, limit }
      // Fallback for older cached array response (defensive)
      const payload = Array.isArray(res.data)
        ? { sessions: res.data, total: res.data.length, truncated: false }
        : res.data;
      const rows = payload.sessions || [];
      cache.current = {
        data: rows,
        total: payload.total ?? rows.length,
        truncated: !!payload.truncated,
        timestamp: Date.now(),
        filters: { status: statusFilter, sortBy, sortOrder },
      };
      setSessions(rows);
      setTotalMatching(payload.total ?? rows.length);
      setTruncated(!!payload.truncated);
    } catch (e) {
      console.error(e);
      if (hasCached && filtersMatch) {
        setSessions(cache.current.data);
        setTotalMatching(cache.current.total);
        setTruncated(cache.current.truncated);
      }
    } finally {
      setLoading(false);
      setBgRefreshing(false);
    }
  };

  useEffect(() => {
    const lastId = sessionStorage.getItem('last_viewed_session_id');
    if (lastId) {
      setSessions(prev => prev.map(s => s._id === lastId ? { ...s, has_unread: false } : s));
      sessionStorage.removeItem('last_viewed_session_id');
    }
    const filtersMatch = cache.current.filters.status === statusFilter &&
                        cache.current.filters.sortBy === sortBy &&
                        cache.current.filters.sortOrder === sortOrder;
    if (cache.current.data.length > 0 && filtersMatch) {
      setSessions(cache.current.data);
      setTotalMatching(cache.current.total);
      setTruncated(cache.current.truncated);
      setLoading(false);
    }
    fetchSessions();
  }, [statusFilter, sortBy, sortOrder, location.key]);

  // WebSocket badge refresh
  useEffect(() => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!token) return;
    const base = (process.env.REACT_APP_BACKEND_URL || '')
      .replace('https://', 'wss://').replace('http://', 'ws://');
    let ws = null, pingTimer = null, reconnectTimer = null, destroyed = false;
    const connect = () => {
      if (destroyed) return;
      try {
        ws = new WebSocket(`${base}/api/ws/notifications?token=${token}`);
        ws.onopen = () => { pingTimer = setInterval(() => { if (ws?.readyState === WebSocket.OPEN) ws.send('ping'); }, 25000); };
        ws.onmessage = (e) => {
          if (e.data === 'pong') return;
          try {
            const m = JSON.parse(e.data);
            if (m.type === 'badge_update') {
              const params = new URLSearchParams();
              if (statusFilter !== 'all') params.set('status', statusFilter);
              params.set('sort_by', sortBy === 'priority' ? 'last_activity' : sortBy);
              params.set('sort_order', sortOrder);
              api.get(`/crm/sessions?${params.toString()}`).then(res => {
                const payload = Array.isArray(res.data)
                  ? { sessions: res.data, total: res.data.length, truncated: false }
                  : res.data;
                const rows = payload.sessions || [];
                cache.current = {
                  data: rows,
                  total: payload.total ?? rows.length,
                  truncated: !!payload.truncated,
                  timestamp: Date.now(),
                  filters: { status: statusFilter, sortBy, sortOrder },
                };
                setSessions(rows);
                setTotalMatching(payload.total ?? rows.length);
                setTruncated(!!payload.truncated);
              }).catch(() => {});
            }
          } catch (err) { /* ignore malformed frames */ }
        };
        ws.onclose = () => { clearInterval(pingTimer); if (!destroyed) reconnectTimer = setTimeout(connect, 4000); };
        ws.onerror = () => ws?.close();
      } catch (err) { /* connection init failed — retry timer will handle */ }
    };
    connect();
    return () => { destroyed = true; clearInterval(pingTimer); clearTimeout(reconnectTimer); ws?.close(); };
  }, [statusFilter, sortBy, sortOrder]);

  // URL sync
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    const url = new URL(window.location.href);
    if (searchQuery) url.searchParams.set('q', searchQuery); else url.searchParams.delete('q');
    if (statusFilter && statusFilter !== 'all') url.searchParams.set('status', statusFilter); else url.searchParams.delete('status');
    if (salesmanFilter) url.searchParams.set('salesman', salesmanFilter); else url.searchParams.delete('salesman');
    if (sortBy && sortBy !== 'priority') url.searchParams.set('sort_by', sortBy); else url.searchParams.delete('sort_by');
    if (sortOrder && sortOrder !== 'desc') url.searchParams.set('sort_order', sortOrder); else url.searchParams.delete('sort_order');
    if (quickFilter) url.searchParams.set('quick', quickFilter); else url.searchParams.delete('quick');
    if (parkActionFilter) url.searchParams.set('park', parkActionFilter); else url.searchParams.delete('park');
    window.history.replaceState(window.history.state, '', url.toString());
  }, [searchQuery, statusFilter, salesmanFilter, sortBy, sortOrder, quickFilter, parkActionFilter]);

  useEffect(() => {
    Promise.all([
      api.get('/users/by-role/salesman').catch(() => ({ data: [] })),
      api.get('/users/by-role/stock_supervisor').catch(() => ({ data: [] })),
      api.get('/crm/park-actions').catch(() => ({ data: [] })),
    ]).then(([s, ss, pa]) => {
      const merged = [...(s.data || []), ...(ss.data || [])];
      const seen = new Set();
      setSalesmen(merged.filter(u => seen.has(u.id) ? false : seen.add(u.id)));
      setParkActions(pa.data || []);
    });
  }, []);

  const handleCreate = async () => {
    if (!form.assigned_salesman_id) {
      toast({ title: 'Error', description: 'Please assign a salesman', variant: 'destructive' });
      return;
    }
    try {
      const res = await api.post('/crm/sessions', form);
      toast({ title: 'Session created' });
      setCreateOpen(false);
      setForm({ assigned_salesman_id: '', lead_source: '', notes: '' });
      invalidateCache();
      fetchSessions(true);
      if (res.data?._id) navigate(`/lead-sessions/${res.data._id}`);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/crm/sessions/${deleteTarget._id}`);
      toast({ title: 'Session deleted' });
      setDeleteTarget(null);
      invalidateCache();
      fetchSessions(true);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to delete', variant: 'destructive' });
    }
  };

  // ── Bulk selection handlers (Admin only) ──
  const enterSelectMode = () => {
    setSelectMode(true);
    setSelectedIds(new Set());
    closeSwipe();
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllFiltered = () => {
    setSelectedIds(new Set(filtered.map(s => s._id)));
  };
  const clearSelection = () => setSelectedIds(new Set());
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const res = await api.post('/crm/sessions/bulk-delete', { session_ids: ids });
      const deleted = res.data?.deleted_sessions ?? ids.length;
      toast({ title: `Deleted ${deleted} session${deleted === 1 ? '' : 's'}` });
      setBulkConfirmOpen(false);
      exitSelectMode();
      invalidateCache();
      fetchSessions(true);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to delete', variant: 'destructive' });
    } finally {
      setBulkDeleting(false);
    }
  };

  // Priority ordering for client-side "priority-first" sort
  const PRIORITY_RANK = { overdue: 0, ready_to_convert: 1, today: 2, later: 3, none: 4 };

  const filtered = sessions.filter(s => {
    // Quick filter chips
    if (quickFilter === 'overdue'          && s.priority !== 'overdue') return false;
    if (quickFilter === 'ready_to_convert' && s.priority !== 'ready_to_convert') return false;
    if (quickFilter === 'today'            && s.priority !== 'today') return false;
    if (quickFilter === 'unread'           && !s.has_unread) return false;
    if (quickFilter === 'action_needed'    && (s.client_name || s.client_phone)) return false;
    if (parkActionFilter && !(s.park_actions || []).includes(parkActionFilter)) return false;
    if (salesmanFilter && salesmanFilter !== 'all' && s.assigned_salesman_name !== salesmanFilter) return false;
    const q = searchQuery.toLowerCase();
    if (!q) return true;
    return s.client_name?.toLowerCase().includes(q) ||
      s.client_phone?.includes(q) ||
      s.session_number?.toLowerCase().includes(q) ||
      s.assigned_salesman_name?.toLowerCase().includes(q) ||
      s.last_message?.toLowerCase().includes(q);
  });

  // Client-side priority-first ordering (only when sortBy = priority)
  if (sortBy === 'priority') {
    filtered.sort((a, b) => {
      const ra = PRIORITY_RANK[a.priority || 'none'];
      const rb = PRIORITY_RANK[b.priority || 'none'];
      if (ra !== rb) return ra - rb;
      // tie-break by last_message_time desc
      const ta = a.last_message_time ? new Date(a.last_message_time).getTime() : 0;
      const tb = b.last_message_time ? new Date(b.last_message_time).getTime() : 0;
      return tb - ta;
    });
  }

  const sessionSalesmenNames = [...new Set(sessions.map(s => s.assigned_salesman_name).filter(Boolean))].sort();

  useEffect(() => {
    const count = sessions.filter(s => s.has_unread).length;
    setBadge(count);
    updateTabTitle(count);
    return () => updateTabTitle(0);
  }, [sessions]);

  // Filter chip counts (from all sessions, not filtered)
  const qCounts = {
    overdue:          sessions.filter(s => s.priority === 'overdue').length,
    ready_to_convert: sessions.filter(s => s.priority === 'ready_to_convert').length,
    today:            sessions.filter(s => s.priority === 'today').length,
    unread:           sessions.filter(s => s.has_unread).length,
    action_needed:    sessions.filter(s => !s.client_name && !s.client_phone).length,
  };

  // Per-park-action session counts (session included when it has at least one
  // parked lead with that action).
  const parkActionCounts = parkActions.reduce((acc, pa) => {
    acc[pa.key] = sessions.filter(s => (s.park_actions || []).includes(pa.key)).length;
    return acc;
  }, {});
  const totalParkActionSessions = sessions.filter(s => (s.park_actions || []).length > 0).length;

  // ── Next Action Ribbon: compute the single most urgent session as the CTA ──
  // Priority: overdue > ready_to_convert > today. Tie-break by oldest last_message_time.
  const nextActionTarget = (() => {
    const rank = { overdue: 0, ready_to_convert: 1, today: 2 };
    const candidates = sessions
      .filter(s => s.priority in rank && (s.client_name || s.client_phone))
      .sort((a, b) => {
        const rd = rank[a.priority] - rank[b.priority];
        if (rd !== 0) return rd;
        const ta = a.last_message_time ? new Date(a.last_message_time).getTime() : 0;
        const tb = b.last_message_time ? new Date(b.last_message_time).getTime() : 0;
        return ta - tb; // oldest first (most stale wins)
      });
    return candidates[0] || null;
  })();

  const totalActionable = qCounts.overdue + qCounts.ready_to_convert + qCounts.today;
  const showRibbon = totalActionable > 0;
  const ribbonPulses = qCounts.overdue > 0 && qCounts.ready_to_convert > 0;
  // ─────────────────────────────────────────────────────────────────────────

  const canCreate = ['admin', 'owner', 'crm', 'salesman', 'stock_supervisor'].includes(currentUser?.role);

  // Filter chip config
  const FILTER_CHIPS = [
    { key: 'overdue',          label: 'Overdue',          activeCls: 'bg-red-500 text-white',       idleCls: 'bg-red-50 text-red-700 border border-red-200' },
    { key: 'ready_to_convert', label: 'Ready to Convert', activeCls: 'bg-emerald-600 text-white',   idleCls: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
    { key: 'today',            label: 'Today',            activeCls: 'bg-amber-500 text-white',     idleCls: 'bg-amber-50 text-amber-700 border border-amber-200' },
    { key: 'unread',           label: 'Unread',           activeCls: 'bg-[#128C7E] text-white',     idleCls: 'bg-teal-50 text-teal-700 border border-teal-200' },
    { key: 'action_needed',    label: 'Action Needed',    activeCls: 'bg-orange-500 text-white',    idleCls: 'bg-orange-50 text-orange-700 border border-orange-200' },
  ];

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="lead-sessions-page">
      {/* Header — WhatsApp green */}
      <div className="bg-[#075E54] text-white px-4 py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                Lead Sessions
                {bgRefreshing && <RefreshCw className="w-3 h-3 animate-spin opacity-70" />}
              </h1>
              <p className="text-xs text-emerald-200" data-testid="sessions-count-label">
                {filtered.length}
                {filtered.length !== sessions.length ? ` of ${sessions.length}` : ''}
                {truncated ? ` (${totalMatching} total)` : (filtered.length === sessions.length && totalMatching > sessions.length ? ` of ${totalMatching}` : '')}
                {' '}session{filtered.length === 1 ? '' : 's'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="text-white hover:bg-emerald-700 h-8 px-2" onClick={() => navigate('/clients')} data-testid="view-clients-btn" title="View all clients">
              <Users className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline text-xs">Clients</span>
            </Button>
            {isAdmin && !selectMode && (
              <Button
                size="sm"
                variant="ghost"
                className="text-white hover:bg-emerald-700 h-8 px-2"
                onClick={enterSelectMode}
                data-testid="enter-select-mode-btn"
                title="Select sessions to delete"
              >
                <CheckSquare className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline text-xs">Select</span>
              </Button>
            )}
            {canCreate && !selectMode && (
              <Button size="sm" className="bg-white text-emerald-700 hover:bg-emerald-50 h-8" onClick={() => {
                if (['salesman', 'stock_supervisor'].includes(currentUser?.role)) {
                  setForm(f => ({ ...f, assigned_salesman_id: currentUser.id }));
                }
                setCreateOpen(true);
              }} data-testid="create-session-btn">
                <Plus className="w-4 h-4 mr-1" /> New Session
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ── Selection action bar (Admin only) ── */}
      {selectMode && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid="bulk-select-bar">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-semibold text-amber-900" data-testid="bulk-select-count">
              {selectedIds.size} selected
            </span>
            <button
              onClick={selectAllFiltered}
              className="text-xs text-amber-800 hover:text-amber-900 underline underline-offset-2"
              data-testid="bulk-select-all-btn"
            >
              Select all visible ({filtered.length})
            </button>
            {selectedIds.size > 0 && (
              <button
                onClick={clearSelection}
                className="text-xs text-amber-800 hover:text-amber-900 underline underline-offset-2"
                data-testid="bulk-clear-btn"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={exitSelectMode}
              className="h-8 text-amber-900 hover:bg-amber-100"
              data-testid="exit-select-mode-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={selectedIds.size === 0}
              onClick={() => setBulkConfirmOpen(true)}
              className="h-8 bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete selected
            </Button>
          </div>
        </div>
      )}

      {/* Next-Action Ribbon — appears only when there's actionable work */}
      {showRibbon && (
        <div
          data-testid="next-action-ribbon"
          className={`relative overflow-hidden bg-gradient-to-r from-[#054A42] via-[#075E54] to-[#0B7A6C] text-white ${ribbonPulses ? 'diaforge-ribbon-glow' : ''}`}
        >
          {/* subtle diagonal gloss */}
          <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
            backgroundImage: 'linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.15) 50%, transparent 60%)'
          }} />
          <div className="relative flex items-center gap-2 px-4 py-2.5">
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${ribbonPulses ? 'bg-amber-400 text-amber-900 animate-pulse' : 'bg-white/20 text-white'}`}>
              <Zap className="w-4 h-4" fill="currentColor" strokeWidth={0} />
            </div>
            <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
              {qCounts.overdue > 0 && (
                <button
                  onClick={() => setQuickFilter('overdue')}
                  className="flex items-center gap-1 font-semibold hover:underline decoration-red-300 underline-offset-2"
                  data-testid="ribbon-overdue-stat"
                >
                  <span className="text-red-300 font-black text-sm leading-none">{qCounts.overdue}</span>
                  <span className="text-red-100">overdue</span>
                </button>
              )}
              {qCounts.overdue > 0 && (qCounts.ready_to_convert > 0 || qCounts.today > 0) && <span className="text-white/40">·</span>}
              {qCounts.ready_to_convert > 0 && (
                <button
                  onClick={() => setQuickFilter('ready_to_convert')}
                  className="flex items-center gap-1 font-semibold hover:underline decoration-emerald-300 underline-offset-2"
                  data-testid="ribbon-ready-stat"
                >
                  <span className="text-emerald-200 font-black text-sm leading-none">{qCounts.ready_to_convert}</span>
                  <span className="text-emerald-50">ready to convert</span>
                </button>
              )}
              {qCounts.ready_to_convert > 0 && qCounts.today > 0 && <span className="text-white/40">·</span>}
              {qCounts.today > 0 && (
                <button
                  onClick={() => setQuickFilter('today')}
                  className="flex items-center gap-1 font-semibold hover:underline decoration-amber-300 underline-offset-2"
                  data-testid="ribbon-today-stat"
                >
                  <span className="text-amber-200 font-black text-sm leading-none">{qCounts.today}</span>
                  <span className="text-amber-50">follow up today</span>
                </button>
              )}
            </div>
            {nextActionTarget && (
              <button
                onClick={() => {
                  if (nextActionTarget.has_unread) markRead(nextActionTarget._id);
                  sessionStorage.setItem('last_viewed_session_id', nextActionTarget._id);
                  navigate(`/lead-sessions/${nextActionTarget._id}`);
                }}
                className="flex-shrink-0 flex items-center gap-1 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-full pl-3 pr-1.5 py-1 text-[11px] font-semibold transition-colors max-w-[45%]"
                data-testid="ribbon-next-cta"
                title={`Open: ${nextActionTarget.client_name || nextActionTarget.session_number}`}
              >
                <span className="text-white/70 uppercase tracking-wide text-[9px] font-bold">Next</span>
                <span className="truncate max-w-[110px]">{nextActionTarget.client_name || nextActionTarget.session_number}</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Filters — priority-first chips */}
      <div className="px-4 py-3 bg-white border-b space-y-2">
        <div className="flex gap-2 overflow-x-auto pb-0.5 scrollbar-hide" data-testid="filter-chips">
          {FILTER_CHIPS.map(chip => (
            <button
              key={chip.key}
              data-testid={`quick-filter-${chip.key}`}
              onClick={() => setQuickFilter(q => q === chip.key ? '' : chip.key)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${quickFilter === chip.key ? chip.activeCls : chip.idleCls}`}
            >
              {chip.label}
              {qCounts[chip.key] > 0 && (
                <span className={`rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-black ${quickFilter === chip.key ? 'bg-white/30' : 'bg-white border'}`}>
                  {qCounts[chip.key]}
                </span>
              )}
            </button>
          ))}
        </div>
        {totalParkActionSessions > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-0.5 scrollbar-hide" data-testid="park-action-chips">
            <button
              data-testid="park-action-filter-all"
              onClick={() => setParkActionFilter('')}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                !parkActionFilter
                  ? 'bg-amber-500 text-white'
                  : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}
            >
              All Parked
              <span className={`rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-black ${!parkActionFilter ? 'bg-white/30' : 'bg-white border'}`}>
                {totalParkActionSessions}
              </span>
            </button>
            {parkActions.map(pa => (
              <button
                key={pa.key}
                data-testid={`park-action-filter-${pa.key}`}
                onClick={() => setParkActionFilter(f => f === pa.key ? '' : pa.key)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                  parkActionFilter === pa.key
                    ? 'bg-amber-600 text-white'
                    : 'bg-amber-50 text-amber-800 border border-amber-200'
                }`}
              >
                {pa.label}
                {parkActionCounts[pa.key] > 0 && (
                  <span className={`rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-black ${parkActionFilter === pa.key ? 'bg-white/30' : 'bg-white border'}`}>
                    {parkActionCounts[pa.key]}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search client, phone, message..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9" data-testid="search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-24 h-9" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Select value={salesmanFilter} onValueChange={setSalesmanFilter}>
            <SelectTrigger className="w-36 h-9" data-testid="salesman-filter">
              <SelectValue placeholder="Salesman" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Salesmen</SelectItem>
              {sessionSalesmenNames.map(name => (
                <SelectItem key={name} value={name}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 flex-1">
            <ArrowUpDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="flex-1 h-9" data-testid="sort-by-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="priority">Priority</SelectItem>
                <SelectItem value="last_activity">Last Activity</SelectItem>
                <SelectItem value="created_date">Created Date</SelectItem>
                <SelectItem value="followup_date">Follow-up Date</SelectItem>
                <SelectItem value="estimated_value">Pipeline Value</SelectItem>
                <SelectItem value="client_name">Client Name</SelectItem>
              </SelectContent>
            </Select>
            <button
              onClick={() => setSortOrder(o => o === 'desc' ? 'asc' : 'desc')}
              className="h-9 w-9 flex-shrink-0 flex items-center justify-center border rounded-md bg-white hover:bg-gray-50 transition-colors text-gray-600"
              title={sortOrder === 'desc' ? 'Descending — click to toggle' : 'Ascending — click to toggle'}
              data-testid="sort-order-toggle"
            >
              {sortOrder === 'desc' ? '↓' : '↑'}
            </button>
          </div>
        </div>
      </div>

      {/* List — WhatsApp-inbox style */}
      <div ref={listRef} className="flex-1 overflow-y-auto" data-testid="sessions-list">
        {loading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-emerald-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No sessions match your filters</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 bg-white">
            {filtered.map(s => {
              const needsClientInfo = !s.client_name && !s.client_phone;
              const hasUnread = s.has_unread === true;
              const isSwiping = swipe.id === s._id;
              const isOpen = openSwipeId === s._id;
              const dx = isSwiping ? swipe.dx : (isOpen ? -140 : 0);
              const priority = s.priority && s.priority !== 'none' ? PRIORITY_CONFIG[s.priority] : null;
              const displayName = needsClientInfo ? 'Untitled session' : s.client_name;
              const call = () => {
                if (!s.client_phone) { toast({ title: 'No phone number' }); return; }
                window.location.href = `tel:${s.client_phone}`;
                closeSwipe();
              };
              const wa = () => {
                if (!s.client_phone) { toast({ title: 'No phone number' }); return; }
                // If this session has any parked leads with a park_action,
                // pre-fill the WhatsApp text using the first one. The salesman
                // can still edit before hitting send.
                const primaryPark = (s.park_actions || [])[0];
                const text = primaryPark
                  ? buildHandoverText({
                      client_name: s.client_name,
                      product_name: s.top_product_name || s.product_name,
                      park_action: primaryPark,
                    })
                  : undefined;
                window.open(waLinkWithText(s.client_phone, text), '_blank');
                closeSwipe();
              };
              const openRow = (e) => {
                if (selectMode) { toggleSelect(s._id); return; }
                if (openSwipeId === s._id) { closeSwipe(); return; }
                if (swipeActive.current) { swipeActive.current = false; return; }
                if (hasUnread) markRead(s._id);
                sessionStorage.setItem('last_viewed_session_id', s._id);
                navigate(`/lead-sessions/${s._id}`);
              };
              const isSelected = selectedIds.has(s._id);
              return (
                <li key={s._id} className="relative overflow-hidden bg-white" data-testid={`session-${s.session_number}`}>
                  {/* ── Right-swipe reveal: Mark as Read (behind row, on LEFT) ── */}
                  {hasUnread && (
                    <div
                      className="absolute inset-y-0 left-0 flex items-center justify-start bg-gradient-to-r from-emerald-600 to-emerald-500 text-white pointer-events-none"
                      style={{
                        width: Math.max(0, Math.min(dx, 160)) + 'px',
                        transition: isSwiping ? 'none' : 'width 0.2s ease',
                      }}
                      data-testid={`right-swipe-bg-${s.session_number}`}
                    >
                      <div className="flex items-center gap-2 pl-4 whitespace-nowrap">
                        <CheckCircle2 className={`w-5 h-5 transition-transform ${dx > 60 ? 'scale-110' : 'scale-90'}`} />
                        {dx > 40 && <span className="text-xs font-semibold">{dx > 60 ? 'Release to mark read' : 'Mark as read'}</span>}
                      </div>
                    </div>
                  )}

                  {/* ── Left-swipe reveal actions (behind the row) ── */}
                  <div className="absolute inset-y-0 right-0 flex items-center gap-0 pointer-events-none">
                    <button
                      onClick={(e) => { e.stopPropagation(); call(); }}
                      className="h-full w-[70px] bg-blue-500 text-white flex flex-col items-center justify-center pointer-events-auto"
                      style={{ transform: `translateX(${140 + Math.min(dx, 0)}px)`, transition: isSwiping ? 'none' : 'transform 0.2s ease' }}
                      data-testid={`swipe-call-${s.session_number}`}
                    >
                      <Phone className="w-5 h-5 mb-0.5" />
                      <span className="text-[10px] font-semibold">Call</span>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); wa(); }}
                      className="h-full w-[70px] bg-[#25D366] text-white flex flex-col items-center justify-center pointer-events-auto"
                      style={{ transform: `translateX(${140 + Math.min(dx, 0)}px)`, transition: isSwiping ? 'none' : 'transform 0.2s ease' }}
                      data-testid={`swipe-wa-${s.session_number}`}
                    >
                      <MessageCircle className="w-5 h-5 mb-0.5" />
                      <span className="text-[10px] font-semibold">WhatsApp</span>
                    </button>
                  </div>

                  {/* ── Row (translated during swipe) ── */}
                  <div
                    onClick={openRow}
                    style={{ transform: `translateX(${selectMode ? 0 : dx}px)`, transition: isSwiping ? 'none' : 'transform 0.2s ease', touchAction: selectMode ? 'auto' : 'pan-y' }}
                    className={`relative px-4 py-3 flex gap-3 cursor-pointer active:bg-gray-50 ${isSelected ? 'bg-emerald-50' : 'bg-white'} ${needsClientInfo ? 'border-l-4 border-l-amber-400' : hasUnread ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-transparent'}`}
                    onTouchStart={selectMode ? undefined : (e) => handleTouchStart(e, s._id)}
                    onTouchMove={selectMode ? undefined : (e) => handleTouchMove(e, s._id, hasUnread)}
                    onTouchEnd={selectMode ? undefined : () => handleTouchEnd(s._id, hasUnread)}
                    data-testid={`session-row-${s.session_number}`}
                  >
                    {/* Selection checkbox (Admin bulk-select mode) */}
                    {selectMode && (
                      <div className="flex-shrink-0 flex items-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(s._id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                          data-testid={`session-checkbox-${s.session_number}`}
                          aria-label={`Select session ${s.session_number}`}
                        />
                      </div>
                    )}

                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold ${needsClientInfo ? 'bg-amber-100 text-amber-700' : 'bg-gradient-to-br from-[#075E54] to-[#128C7E] text-white'}`}>
                        {needsClientInfo ? <AlertTriangle className="w-5 h-5" /> : initialsOf(displayName)}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Row 1: name + timestamp */}
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`truncate text-sm ${hasUnread ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'}`}>{displayName}</span>
                          {hasUnread && <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" data-testid={`unread-dot-${s.session_number}`} />}
                        </div>
                        <span className={`text-[11px] flex-shrink-0 ${hasUnread ? 'text-emerald-600 font-semibold' : 'text-gray-400'}`}>
                          {formatWhen(s.last_message_time)}
                        </span>
                      </div>

                      {/* Row 2: last message preview */}
                      <div className="flex items-center justify-between gap-2 mt-0.5">
                        <p className={`text-xs truncate ${hasUnread ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                          {needsClientInfo
                            ? <span className="italic text-amber-600">Add client details to activate</span>
                            : (s.last_message || <span className="italic text-gray-400">No messages yet</span>)}
                        </p>
                        {s.total_pipeline_value > 0 && (
                          <span className="text-[11px] font-bold text-gray-700 flex-shrink-0" data-testid={`pipeline-value-${s.session_number}`}>
                            {formatValue(s.total_pipeline_value)}
                          </span>
                        )}
                      </div>

                      {/* Row 3: priority + status counts + phone */}
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        {priority && (
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${priority.className}`} data-testid={`priority-${s.priority}-${s.session_number}`}>
                            <priority.Icon className="w-3 h-3" />
                            {priority.label}
                          </span>
                        )}
                        {s.lead_counts && (
                          <div className="flex gap-1">
                            {s.lead_counts.won > 0 && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-bold">{s.lead_counts.won}W</span>}
                            {s.lead_counts.open > 0 && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-bold">{s.lead_counts.open}O</span>}
                            {s.lead_counts.parked > 0 && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold">{s.lead_counts.parked}P</span>}
                            {s.lead_counts.lost > 0 && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">{s.lead_counts.lost}L</span>}
                          </div>
                        )}
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_COLORS[s.status] || STATUS_COLORS.closed}`}>
                          {s.status}
                        </span>
                        {s.assigned_salesman_name && (
                          <span className="text-[10px] text-gray-400 ml-auto">{s.assigned_salesman_name}</span>
                        )}
                        {currentUser?.role === 'admin' && !selectMode && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }}
                            className="ml-1 p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Delete session (admin)"
                            data-testid={`delete-session-${s.session_number}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-session-dialog">
          <DialogHeader>
            <DialogTitle>New Lead Session</DialogTitle>
            <DialogDescription>Client name & phone can be added inside the session.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Assign Salesman *</Label>
              <Select value={form.assigned_salesman_id} onValueChange={v => setForm(f => ({...f, assigned_salesman_id: v}))}>
                <SelectTrigger data-testid="salesman-select"><SelectValue placeholder="Select salesman" /></SelectTrigger>
                <SelectContent>
                  {salesmen.map(sm => (
                    <SelectItem key={sm.id} value={sm.id}>{sm.username}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Lead Source</Label>
              <div className="flex gap-3 flex-wrap">
                {LEAD_SOURCES.map(src => (
                  <label key={src} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    form.lead_source === src ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="lead_source"
                      value={src}
                      checked={form.lead_source === src}
                      onChange={() => setForm(f => ({...f, lead_source: src}))}
                      className="w-4 h-4 text-emerald-600"
                      data-testid={`lead-source-${src.toLowerCase().replace(/\s/g, '-')}`}
                    />
                    <span className="text-sm font-medium">{src}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2} className="w-full px-3 py-2 border rounded-md text-sm" placeholder="Session notes..." data-testid="session-notes-textarea" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#075E54] hover:bg-[#064E46]" data-testid="save-session-btn">Create Session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Session?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete session &quot;{deleteTarget?.session_number}&quot;{deleteTarget?.client_name ? ` (${deleteTarget.client_name})` : ''} and all its leads, messages, notes, AI drafts, voice recordings and related records. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation (Admin) */}
      <AlertDialog open={bulkConfirmOpen} onOpenChange={(open) => { if (!open && !bulkDeleting) setBulkConfirmOpen(false); }}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} session{selectedIds.size === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block mb-2">
                This will <span className="font-semibold text-red-700">permanently delete</span> the selected sessions along with every related record — leads, chat messages, notes, AI drafts, voice recordings, exchange estimates, stock notifications and read-tracking.
              </span>
              {selectedIds.size > 0 && (
                <span className="block bg-gray-50 border border-gray-200 rounded p-2 max-h-32 overflow-y-auto text-xs text-gray-700">
                  {filtered
                    .filter(s => selectedIds.has(s._id))
                    .slice(0, 20)
                    .map(s => (
                      <span key={s._id} className="block truncate">
                        • {s.session_number}{s.client_name ? ` — ${s.client_name}` : ''}
                      </span>
                    ))}
                  {selectedIds.size > 20 && (
                    <span className="block italic text-gray-500 mt-1">…and {selectedIds.size - 20} more</span>
                  )}
                </span>
              )}
              <span className="block mt-2 font-semibold text-red-700">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting} data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleting || selectedIds.size === 0}
              className="bg-red-600 hover:bg-red-700"
              data-testid="bulk-delete-confirm"
            >
              {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LeadSessions;
